import { defineStore } from 'pinia'
import useRouter from '@/helpers/useRouter'
import { computed, ref } from 'vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'

const routeChangeListeners = []

const allowedKeys = [
  'fullPath',
  'hash',
  'href',
  'meta',
  'name',
  'params',
  'path',
  'query',
  'props',
  'children',
  'redirect',
  'matched',
]
const anyKeysAllowed = ['params', 'props', 'query', 'meta']

const cleanRoute = (routeObj) => {
  if (Array.isArray(routeObj)) {
    return routeObj.map((routeObjItem) => cleanRoute(routeObjItem))
  }
  if (typeof routeObj === 'object') {
    const cleaned = {}
    const keys = Object.keys(routeObj)
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i]
      if (allowedKeys.includes(key)) {
        if (anyKeysAllowed.includes(key)) {
          cleaned[key] = routeObj[key]
        } else {
          cleaned[key] = cleanRoute(routeObj[key])
        }
      }
    }
    return cleaned
  }
  return routeObj
}

const useRouterStore = defineStore('router', {
  state: () => {
    const router = useRouter()

    // Keep only properties and sub-properties that may be needed in our app, and not
    // the ones used only internally by vue-router, so that Vue doesn't show warnings
    // when the devtools enumerates the keys containing components because the store
    // is being debugged
    const route = computed(() => cleanRoute(router.currentRoute.value))
    const prevRoute = ref(null)

    // Listen for route change
    router.afterEach((to, from) => {
      if (to.fullPath !== from.fullPath) {
        dev.log(`Route changed from ${from.fullPath} to ${to.fullPath}`)
        if (window.history.state.back) {
          prevRoute.value = cleanRoute(router.resolve(window.history.state.back))
        } else {
          prevRoute.value = null
        }
        for (let i = 0; i < routeChangeListeners.length; i += 1) {
          const routeChangeListener = routeChangeListeners[i]
          routeChangeListener(to, from)
        }
      }
    })

    return {
      route,
      prevRoute,
    }
  },
  actions: {
    /**
     * Programmatically navigate to a new URL by pushing an entry in the history stack.
     *
     * @param to — Route location to navigate to
     */
    push(to) {
      const router = useRouter()
      return router.push(to)
    },
    /**
     * Programmatically navigate to a new URL by replacing the current entry in the history stack.
     *
     * @param to - Route location to navigate to
     */
    replace(to) {
      const router = useRouter()
      return router.replace(to)
    },
    /**
     * Returns the normalized version of a route location.
     * Also includes an href property that includes any existing base.
     *
     * @param {*} to — Raw route location to resolve
     * @returns Resolved route
     */
    resolve(to) {
      const router = useRouter()
      return router.resolve(to)
    },
    /**
     * Calls a function when the route changes
     * @param {Function} handler - Callback when the route changed
     */
    onRouteChange(handler) {
      routeChangeListeners.push(handler)
      const removeRouteChangeListener = () => {
        routeChangeListeners.splice(
          routeChangeListeners.findIndex(handler),
          1,
        )
      }
      return removeRouteChangeListener
    },
  },
})

export default useRouterStore
